// src/services/NotificationService.js
import { createNotificationsClient } from '@liquid-spirit/api/domains/notifications';
import apiClient, { resolveAuthToken as resolveApiAuthToken } from './apiClient';

const requireToken = (overrideToken) => {
  const token = resolveApiAuthToken(overrideToken);
  if (!token) {
    throw new Error('Token not available');
  }
  return token;
};

// Route notification endpoints through the shared domain client. The web app
// keeps its payload contract; paths and request shapes live in the package.
const notifications = createNotificationsClient({
  get: (path, options = {}) => apiClient.get(path, { contract: 'payload', ...options }),
  post: (path, body, options = {}) => apiClient.post(path, body, { contract: 'payload', ...options }),
  patch: (path, body, options = {}) => apiClient.patch(path, body, { contract: 'payload', ...options }),
});

const NotificationService = {
  async sendNotification(type, actorId, targetId, targetType, recipientCommunity, additionalData, scope) {
    requireToken();
    return notifications.send({
      type,
      actor: actorId,
      target: targetId,
      targetType,
      recipientCommunity,
      additionalData,
      scope,
    });
  },

  async userJoinedEventNotification(actorId, eventId, recipientCommunity, eventTitle, userName) {
    return this.sendNotification(
      'join_event',
      actorId,
      eventId,
      'Event',
      recipientCommunity,
      { caption: `${userName} is attending "${eventTitle}".` },
      'community'
    );
  },

  async newActivityNotification(actorId, activityId, recipientCommunity, activityTitle) {
    return this.sendNotification(
      'new_activity',
      actorId,
      activityId,
      'Activity',
      recipientCommunity,
      { caption: `A new activity "${activityTitle}" has been created.` },
      'community'
    );
  },

  async userJoinedActivity(actorId, activityId, recipientCommunity, activityTitle, activityType, userName) {
    return this.sendNotification(
      'activity_updated',
      actorId,
      activityId,
      'Activity',
      recipientCommunity,
      { caption: `${userName} has joined the ${activityType} activity: ${activityTitle}` },
      'community'
    );
  },

  async activityUpdateNotification(actorId, activityId, recipientCommunity, activityTitle) {
    return this.sendNotification(
      'activity_updated',
      actorId,
      activityId,
      'Activity',
      recipientCommunity,
      { caption: `The activity "${activityTitle}" has been updated.` },
      'community'
    );
  },

  async activityCanceledNotification(actorId, activityId, recipientCommunity, activityTitle) {
    return this.sendNotification(
      'activity_canceled',
      actorId,
      activityId,
      'Activity',
      recipientCommunity,
      { caption: `The activity "${activityTitle}" has been canceled.` },
      'community'
    );
  },

  async userLeftEventNotification(actorId, eventId, recipientCommunity, eventTitle, userName) {
    return this.sendNotification(
      'leave_event',
      actorId,
      eventId,
      'Event',
      recipientCommunity,
      { caption: `${userName} has left the event "${eventTitle}".` },
      'community'
    );
  },

  async mediaPostedNotification(actorId, mediaId, recipientCommunity, mediaTitle, mediaUrl) {
    return this.sendNotification(
      'post_media',
      actorId,
      mediaId,
      'Media',
      recipientCommunity,
      { caption: `A new media "${mediaTitle}" has been posted.`, mediaUrl },
      'community'
    );
  },

  async assemblyAnnouncementNotification(actorId, announcementId, recipientCommunity, announcementTitle) {
    return this.sendNotification(
      'assembly_announcement',
      actorId,
      announcementId,
      'Announcement',
      recipientCommunity,
      { caption: `New assembly announcement: "${announcementTitle}".` },
      'community'
    );
  },

  async budgetUpdateNotification(actorId, budgetId, recipientCommunity) {
    return this.sendNotification(
      'budget_update',
      actorId,
      budgetId,
      'Finance',
      recipientCommunity,
      { caption: 'The community budget has been updated.' },
      'community'
    );
  },

  async eventReminderNotification(actorId, eventId, recipientCommunity, eventTitle) {
    return this.sendNotification(
      'event_reminder',
      actorId,
      eventId,
      'Event',
      recipientCommunity,
      { caption: `Reminder: The event "${eventTitle}" is happening soon.` },
      'community'
    );
  },

  async newMemberWelcomeNotification(actorId, memberId, recipientCommunity, memberName) {
    return this.sendNotification(
      'new_member_welcome',
      actorId,
      memberId,
      'Member',
      recipientCommunity,
      { caption: `Welcome ${memberName} to the community!` },
      'community'
    );
  },

  async roleAssignmentNotification(actorId, memberId, recipientCommunity, roleName) {
    return this.sendNotification(
      'role_assignment',
      actorId,
      memberId,
      'Member',
      recipientCommunity,
      { caption: `${roleName} role has been assigned.` },
      'community'
    );
  },

  async communityEventUpdateNotification(actorId, eventId, recipientCommunity, eventTitle, updateDetails) {
    return this.sendNotification(
      'community_event_update',
      actorId,
      eventId,
      'Event',
      recipientCommunity,
      { caption: `Update on the event "${eventTitle}": ${updateDetails}` },
      'community'
    );
  },

  async venueRequestNotification(actorId, sessionId, recipientId, recipientCommunity, options = {}) {
    const {
      caption = 'You have a new venue request to review.',
      venueId,
      venueName,
      householdId,
    } = options || {};

    const additionalData = {
      caption,
      recipientId,
      venueId,
      venueName,
      householdId,
    };

    return this.sendNotification(
      'venue_request',
      actorId,
      sessionId,
      'Session',
      recipientCommunity,
      additionalData,
      'personal'
    );
  },

  async markNotificationAsRead(notificationId, opts = {}) {
    const token = requireToken(opts.token);
    return notifications.markRead(notificationId, { ...opts, token, skipMutationActivity: true });
  },

  async markAllNotificationsAsRead(userId, opts = {}) {
    const token = requireToken(opts.token);
    return notifications.markAllRead(userId, { ...opts, token, skipMutationActivity: true });
  },

  async getAllNotifications(userId, params = {}, opts = {}) {
    const token = requireToken(opts.token || params.token);
    const { token: _omitToken, ...queryParams } = params || {};
    return notifications.list({ userId, ...queryParams }, { token });
  }
};

export default NotificationService;
